import {
  Children,
  type ComponentProps,
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

import { isEmptyReactNode } from './is-empty-react-node';

type DefaultElement = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface SlotChildPropsInterface {
  asChild?: boolean;
  children?: ReactNode;
}

export interface DecorateSlotChildrenOptionsInterface<ElementType extends DefaultElement = 'div'> {
  /** The children nodes to be decorated. */
  children: ReactNode;

  /** If true, decorates the first valid ReactElement child. */
  asChild?: boolean;

  /** Callback returning props to merge onto the cloned child when `asChild` is true. */
  childProps?: (child: ReactElement) => Partial<ComponentProps<ElementType>> | undefined;

  /** Optional content overriding `children` or `child.props.children`. Takes precedence. */
  alternativeContent?: ReactNode;

  /** Content used when the selected content source is empty. */
  fallback?: ReactNode;
}

export type SlotChildrenDecoratorType = (childContent: ReactNode) => ReactNode;

const getContentWithFallback = (content: ReactNode, fallback?: ReactNode) =>
  fallback !== undefined && isEmptyReactNode(content) ? fallback : content;

const getFirstValidElement = (children: ReactNode): ReactElement<SlotChildPropsInterface> | null =>
  (Children.toArray(children).find(isValidElement) as ReactElement<SlotChildPropsInterface>) ??
  null;

const isSlotPrimitiveLikeElement = (element: ReactElement<SlotChildPropsInterface>) => {
  const elementType = element.type;

  if (typeof elementType === 'string') {
    return false;
  }

  const displayName = (elementType as { displayName?: string }).displayName;

  return (
    displayName === 'Slot' ||
    displayName?.endsWith('.Slot') === true ||
    displayName === 'Slottable' ||
    displayName?.endsWith('.Slottable') === true
  );
};

const shouldDecorateNestedChild = (element: ReactElement<SlotChildPropsInterface>) =>
  element.props.asChild === true || isSlotPrimitiveLikeElement(element);

/**
 * Decorates React children while preserving Radix Slot composition semantics.
 * When `asChild` is true, it decorates the slotted child content instead of
 * wrapping the slotted root, and recurses through nested `asChild`/Slot nodes.
 */
export function decorateSlotChildren<ElementType extends DefaultElement = 'div'>(
  {
    children,
    asChild = false,
    childProps,
    alternativeContent,
    fallback,
  }: DecorateSlotChildrenOptionsInterface<ElementType>,
  decorator: SlotChildrenDecoratorType,
): ReactNode {
  if (!asChild) {
    const contentToUse = alternativeContent !== undefined ? alternativeContent : children;
    return decorator(getContentWithFallback(contentToUse, fallback));
  }

  const child = getFirstValidElement(children);

  if (!child) {
    return null;
  }

  const decoratedContent = shouldDecorateNestedChild(child)
    ? decorateSlotChildren(
        {
          children: child.props.children,
          asChild: true,
          alternativeContent,
          fallback,
        },
        decorator,
      )
    : decorator(
        getContentWithFallback(
          alternativeContent !== undefined ? alternativeContent : child.props.children,
          fallback,
        ),
      );

  return cloneElement(
    child,
    childProps?.(child) as Partial<typeof child.props> | undefined,
    decoratedContent,
  );
}
